import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Radar, AtSign, Search, X, ArrowLeft, Copy, ChevronRight,
  Image as ImageIcon, CheckCircle2, AlertTriangle, Users, ListChecks, AlertCircle, ImageOff, RefreshCw,
  Folder,
} from '../../ui/icons';
import Card from '../../ui/Card';
import AttributionBadge, { LocationTag, USER_CARD, UserCardFooter, useAttributions, useUserLocations } from '../../ui/AttributionBadge';
import Button from '../../ui/Button';
import Colors, { withAlpha } from '../../../constants/Colors';
import { copyToClipboard } from '../../../utils/clipboard';
import {
  opsSyncApi,
  OpsSyncUser,
  OpsSyncAttempt,
  OpsSyncRequestDetail,
  OpsSyncGame,
  OpsSyncImageFile,
} from '../../../services/api';

type PanelTab = 'detections' | 'images';

const PANEL_TABS: { key: PanelTab; label: string }[] = [
  { key: 'detections', label: 'Sync Detections' },
  { key: 'images', label: 'Sync Images' },
];

// Sync-images bucket layout: game_type/game_name/user_email/attempt_id/image.jpg
const IMAGE_LEVEL_LABELS = ['Game Type', 'Game', 'User Email', 'Image'];

// Labelled cells for a request where the Bedrock LLM re-read the scoreboard
// after Genius matching failed (same fields as the lambda's combined_request_summary log).
const formatClock = (time?: string, period?: string) =>
  time ? `${time}${period ? ` P${period}` : ''}` : '—';

const llmFallbackFields = (r: OpsSyncRequestDetail): { label: string; value: string }[] => [
  { label: 'Trigger error', value: r.llm_fallback_trigger_error || '—' },
  { label: 'Original clock', value: formatClock(r.llm_fallback_original_game_time, r.llm_fallback_original_period) },
  { label: 'LLM clock', value: formatClock(r.llm_fallback_game_time, r.llm_fallback_period) },
  { label: 'Time difference', value: `${r.time_difference_seconds.toFixed(2)}s` },
  { label: 'Attempted', value: r.llm_fallback_attempted ? 'True' : 'False' },
  { label: 'Used', value: r.llm_fallback_used ? 'True' : 'False' },
];

function SyncImagesBrowser() {
  const [path, setPath] = useState<string[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  const [files, setFiles] = useState<OpsSyncImageFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingFile, setViewingFile] = useState<OpsSyncImageFile | null>(null);
  const [filterText, setFilterText] = useState('');

  const prefix = path.join('/');

  const load = useCallback(async (p: string) => {
    setLoading(true);
    const response = await opsSyncApi.browseImages(p);
    if (response.success && response.data?.success) {
      setError(null);
      setFolders(response.data.folders || []);
      setFiles(response.data.files || []);
    } else {
      setError(response.error || response.data?.error || 'Failed to browse sync images');
      setFolders([]);
      setFiles([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(prefix);
    setFilterText('');
  }, [prefix, load]);

  const levelLabel = IMAGE_LEVEL_LABELS[path.length] || 'Image';

  const filteredFolders = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    if (!q) return folders;
    return folders.filter((f) => f.toLowerCase().includes(q));
  }, [folders, filterText]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <button
          type="button"
          onClick={() => setPath([])}
          style={{
            border: 0, background: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: '6px',
            fontSize: '13px', fontWeight: path.length === 0 ? 700 : 500,
            color: path.length === 0 ? Colors.text : Colors.secondary,
          }}
        >
          All
        </button>
        {path.map((segment, i) => (
          <React.Fragment key={`${segment}-${i}`}>
            <ChevronRight size={13} style={{ color: Colors.textLight }} />
            <button
              type="button"
              onClick={() => setPath(path.slice(0, i + 1))}
              style={{
                border: 0, background: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: '6px',
                fontSize: '13px', fontFamily: "'SF Mono', monospace",
                fontWeight: i === path.length - 1 ? 700 : 500,
                color: i === path.length - 1 ? Colors.text : Colors.secondary,
                maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              title={segment}
            >
              {segment}
            </button>
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
          <AlertCircle size={20} />
          <div>{error}</div>
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '40px', justifyContent: 'center', color: Colors.textLight }}>
          <span className="loading-spinner" aria-hidden="true" />
          Loading {levelLabel.toLowerCase()}…
        </div>
      ) : folders.length === 0 && files.length === 0 && !error ? (
        <div style={{ textAlign: 'center', padding: '40px', color: Colors.textLight }}>
          <Folder size={28} style={{ opacity: 0.4, marginBottom: '8px' }} />
          <div>No {levelLabel.toLowerCase()} found under this path.</div>
        </div>
      ) : (
        <>
          {folders.length > 0 && (
            <>
              <div style={{ position: 'relative', maxWidth: '360px', marginBottom: '14px' }}>
                <Search size={14} style={{ position: 'absolute', left: '11px', top: '11px', color: Colors.inactive }} />
                <input
                  className="form-input"
                  style={{ paddingLeft: '32px' }}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  placeholder={`Filter by ${levelLabel.toLowerCase()}…`}
                />
              </div>

              {filteredFolders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: Colors.textLight }}>
                  No {levelLabel.toLowerCase()} match &quot;{filterText.trim()}&quot;.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '10px', marginBottom: files.length > 0 ? '20px' : 0 }}>
                  {filteredFolders.map((folder) => (
                    <button
                      key={folder}
                      type="button"
                      onClick={() => setPath([...path, folder])}
                      className="card"
                      style={{
                        textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '10px',
                        padding: '12px 14px', boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.16)',
                      }}
                    >
                      <Folder size={18} style={{ color: Colors.secondary, flex: 'none', marginTop: '1px' }} />
                      <span
                        title={folder}
                        style={{
                          fontFamily: "'SF Mono', monospace", fontSize: '13px',
                          overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal',
                        }}
                      >
                        {folder}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {files.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '14px' }}>
              {files.map((file) => (
                <div
                  key={file.key}
                  className="card"
                  style={{ padding: '10px', boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.16)', display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  <button
                    type="button"
                    onClick={() => setViewingFile(file)}
                    style={{ border: 0, padding: 0, background: 'none', cursor: 'pointer', borderRadius: '8px', overflow: 'hidden' }}
                  >
                    <img
                      src={file.url}
                      alt={file.name}
                      style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block', background: Colors.background }}
                    />
                  </button>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {file.attempt_id && (
                      <span
                        title={file.attempt_id}
                        style={{
                          fontSize: '10.5px', fontWeight: 700, color: Colors.secondary,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        Attempt: {file.attempt_id}
                      </span>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        title={file.name}
                        style={{
                          flex: 1, minWidth: 0, fontSize: '11px', fontFamily: "'SF Mono', monospace",
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: Colors.textLight,
                        }}
                      >
                        {file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(file.key, 'S3 key copied')}
                        title="Copy S3 key"
                        style={{
                          flex: 'none', width: '22px', height: '22px', padding: 0, border: 0, background: 'transparent',
                          display: 'grid', placeItems: 'center', cursor: 'pointer', color: Colors.inactive, borderRadius: '4px',
                        }}
                      >
                        <Copy size={13} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {viewingFile && (
        <div className="games-modal-overlay" onClick={() => setViewingFile(null)}>
          <div className="games-modal-panel" style={{ width: 'min(720px, calc(100vw - 32px))' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: Colors.text }}>
                  Screenshot{viewingFile.attempt_id ? ` · Attempt ${viewingFile.attempt_id}` : ''}
                </div>
                <div
                  style={{
                    fontSize: '12px', color: Colors.textLight, marginTop: '2px', fontFamily: "'SF Mono', monospace",
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                  title={viewingFile.key}
                >
                  {viewingFile.key}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', flex: 'none' }}>
                <Button variant="outline" size="small" onClick={() => copyToClipboard(viewingFile.key, 'S3 key copied')}>
                  <Copy size={13} />
                  Copy key
                </Button>
                <button
                  type="button"
                  onClick={() => setViewingFile(null)}
                  style={{
                    width: '30px', height: '30px', borderRadius: '999px', border: '1px solid rgba(var(--color-border-rgb), 0.24)',
                    background: '#FFFFFF', display: 'grid', placeItems: 'center', cursor: 'pointer', color: Colors.text,
                  }}
                >
                  <X size={15} />
                </button>
              </div>
            </div>
            <img
              src={viewingFile.url}
              alt={viewingFile.name}
              style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '10px', background: Colors.background, display: 'block' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

type PresetRange = 5 | 15 | 30 | 60 | 120;
type RangeMode = 'preset' | 'custom';

const RANGE_OPTIONS: PresetRange[] = [5, 15, 30, 60, 120];
const POLL_INTERVAL_MS = 5000;
const MAX_CUSTOM_MINUTES = 10080; // 7 days

const RESULT_TONE: Record<string, 'status-success' | 'status-warning' | 'status-error'> = {
  sync_ok: 'status-success',
  USER_AHEAD_OF_LIVE: 'status-warning',
  GENIUS_TOO_MANY_MATCHES: 'status-warning',
  GENIUS_GAME_TIME_PAUSED: 'status-warning',
  GAME_NOT_STARTED: 'status-warning',
  USER_FAR_BEHIND_LIVE: 'status-error',
  GENIUS_NO_MATCH: 'status-error',
};

// Known result codes always offered in the result filter, even before any data has loaded -
// any other value actually seen in the loaded requests (e.g. a code not listed here) is added
// to the filter options dynamically, so nothing becomes unfilterable.
const KNOWN_RESULT_VALUES = [
  'sync_ok',
  'GENIUS_GAME_TIME_PAUSED',
  'GENIUS_TOO_MANY_MATCHES',
  'GENIUS_NO_MATCH',
  'USER_FAR_BEHIND_LIVE',
  'USER_AHEAD_OF_LIVE',
  'unknown_error',
];

const ATTEMPT_TONE: Record<string, 'status-success' | 'status-warning' | 'status-error'> = {
  success: 'status-success',
  partial: 'status-warning',
  failed: 'status-error',
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: Colors.textLight,
  marginBottom: '6px',
};

function formatRangeLabel(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}h ${mins}m`;
}

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultCustomFromValue(): string {
  return toDatetimeLocalValue(new Date(Date.now() - 24 * 60 * 60 * 1000));
}

function minutesFromCustomFrom(fromLocal: string): number | null {
  if (!fromLocal.trim()) return null;
  const from = new Date(fromLocal);
  if (Number.isNaN(from.getTime())) return null;
  const diffMs = Date.now() - from.getTime();
  if (diffMs <= 0) return null;
  return Math.min(MAX_CUSTOM_MINUTES, Math.max(1, Math.ceil(diffMs / 60000)));
}

export default function SyncDetectionPanel() {
  const [activeTab, setActiveTab] = useState<PanelTab>('detections');
  const [rangeMode, setRangeMode] = useState<RangeMode>('preset');
  const [range, setRange] = useState<PresetRange>(60);
  const [customFrom, setCustomFrom] = useState(defaultCustomFromValue);
  const [debouncedCustomFrom, setDebouncedCustomFrom] = useState(customFrom);
  const [userQuery, setUserQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [gameName, setGameName] = useState('');

  const [users, setUsers] = useState<OpsSyncUser[]>([]);
  const attributionFor = useAttributions(users.map((u) => u.user_email));
  const locationFor = useUserLocations(users.map((u) => u.user_email));
  const renderUserCardFooter = (email: string) => (
    <UserCardFooter location={locationFor(email)} attribution={attributionFor(email)} />
  );
  const [unsyncedUsers, setUnsyncedUsers] = useState<OpsSyncUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);

  const [gameOptions, setGameOptions] = useState<OpsSyncGame[]>([]);

  const [attempts, setAttempts] = useState<OpsSyncAttempt[]>([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [attemptsError, setAttemptsError] = useState<string | null>(null);
  const [resultFilter, setResultFilter] = useState<Set<string>>(new Set());

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [copiedAttemptId, setCopiedAttemptId] = useState<string | null>(null);
  const [viewingImage, setViewingImage] = useState<OpsSyncRequestDetail | null>(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);
  const [refreshingImageUrl, setRefreshingImageUrl] = useState(false);

  // Debounce custom From so spinning the datetime minutes doesn't fire an API call per tick.
  useEffect(() => {
    if (rangeMode !== 'custom') {
      setDebouncedCustomFrom(customFrom);
      return;
    }
    const timer = window.setTimeout(() => {
      setDebouncedCustomFrom(customFrom);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [customFrom, rangeMode]);

  const effectiveMinutes = useMemo(() => {
    if (rangeMode === 'custom') {
      return minutesFromCustomFrom(debouncedCustomFrom) ?? range;
    }
    return range;
  }, [rangeMode, debouncedCustomFrom, range]);

  const customRangeInvalid = rangeMode === 'custom' && minutesFromCustomFrom(customFrom) === null;
  const fetchBlocked = rangeMode === 'custom' && minutesFromCustomFrom(debouncedCustomFrom) === null;
  const liveCustomMinutes = useMemo(() => minutesFromCustomFrom(customFrom), [customFrom]);

  const fetchUsers = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setUsersLoading(true);
    const response = await opsSyncApi.getUsers(effectiveMinutes, gameName || undefined);
    if (response.success && response.data?.success) {
      setUsersError(null);
      const fetchedUsers = response.data.users || [];
      setUsers(fetchedUsers);
      const unsynced = response.data.unsynced_users || fetchedUsers.filter((u) => u.is_last_attempt_failed || (u.failures > 0 && u.failures === (u.total_attempts || u.total_requests)));
      setUnsyncedUsers(unsynced);
    } else {
      setUsersError(response.error || response.data?.error || 'Failed to load active users');
    }
    if (showSpinner) setUsersLoading(false);
  }, [effectiveMinutes, gameName]);

  const fetchAttempts = useCallback(async (email: string, showSpinner: boolean) => {
    if (showSpinner) setAttemptsLoading(true);
    const response = await opsSyncApi.getAttempts(email, effectiveMinutes, gameName || undefined);
    if (response.success && response.data?.success) {
      setAttemptsError(null);
      setAttempts(response.data.attempts || []);
    } else {
      setAttemptsError(response.error || response.data?.error || 'Failed to load sync attempts');
    }
    if (showSpinner) setAttemptsLoading(false);
  }, [effectiveMinutes, gameName]);

  const fetchGames = useCallback(async () => {
    const response = await opsSyncApi.getGames(effectiveMinutes);
    if (response.success && response.data?.success) {
      setGameOptions(response.data.games || []);
    }
  }, [effectiveMinutes]);

  useEffect(() => {
    if (fetchBlocked) return;
    if (selectedUser) {
      fetchAttempts(selectedUser, true);
    } else {
      fetchUsers(true);
    }
    fetchGames();
  }, [selectedUser, fetchUsers, fetchAttempts, fetchGames, fetchBlocked]);

  useEffect(() => {
    if (fetchBlocked) return;
    const timer = setInterval(() => {
      if (selectedUser) {
        fetchAttempts(selectedUser, false);
      } else {
        fetchUsers(false);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [selectedUser, fetchUsers, fetchAttempts, fetchBlocked]);

  const userMatches = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.user_email.toLowerCase().includes(q));
  }, [users, userQuery]);

  const stats = useMemo(() => {
    const totalAttempts = users.reduce((sum, u) => sum + (u.total_attempts || u.total_requests), 0);
    const totalFailures = users.reduce((sum, u) => sum + u.failures, 0);
    const totalSuccess = Math.max(0, totalAttempts - totalFailures);
    return [
      { label: 'Users trying to sync', value: String(users.length), sub: 'distinct user_id', icon: Users },
      { label: 'Total attempts', value: String(totalAttempts), sub: 'grouped by attempt_id', icon: ListChecks },
      { label: 'Success', value: String(totalSuccess), sub: 'attempt succeeded', icon: CheckCircle2, tone: Colors.success },
      { label: 'Failures', value: String(totalFailures), sub: `${users.filter((u) => u.failures > 0).length} users affected`, icon: AlertTriangle, tone: Colors.error },
    ];
  }, [users]);

  // Every result code worth offering as a filter: the known codes plus anything actually
  // present in the loaded requests that isn't already in that list (e.g. a new code this
  // list hasn't been updated for yet).
  const resultOptions = useMemo(() => {
    const seen = new Set<string>(KNOWN_RESULT_VALUES);
    attempts.forEach((a) => a.requests.forEach((r) => { if (r.result) seen.add(r.result); }));
    return KNOWN_RESULT_VALUES.filter((v) => seen.has(v)).concat(
      Array.from(seen).filter((v) => !KNOWN_RESULT_VALUES.includes(v)).sort()
    );
  }, [attempts]);

  const toggleResultFilter = (value: string) => {
    setResultFilter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  // When a result filter is active, drop non-matching requests from each attempt and recompute
  // that attempt's own counts/status from what's left, so the header badges stay consistent with
  // the rows actually shown - then drop attempts left with zero matching requests.
  const filteredAttempts = useMemo(() => {
    if (resultFilter.size === 0) return attempts;
    return attempts.reduce<OpsSyncAttempt[]>((acc, a) => {
      const requests = a.requests.filter((r) => resultFilter.has(r.result));
      if (requests.length === 0) return acc;
      const success_count = requests.filter((r) => r.success).length;
      const failure_count = requests.length - success_count;
      const status: OpsSyncAttempt['status'] = failure_count === 0 ? 'success' : success_count === 0 ? 'failed' : 'partial';
      acc.push({ ...a, requests, success_count, failure_count, request_count: requests.length, status });
      return acc;
    }, []);
  }, [attempts, resultFilter]);

  const attemptStats = useMemo(() => {
    const totalAttempts = attempts.length;
    const failedAttempts = attempts.filter((a) => a.status === 'failed' || (a.failure_count > 0 && a.success_count === 0)).length;
    const successfulAttempts = Math.max(0, totalAttempts - failedAttempts);
    return {
      attempts: totalAttempts,
      success: successfulAttempts,
      failures: failedAttempts,
    };
  }, [filteredAttempts]);

  const allExpanded = filteredAttempts.length > 0 && filteredAttempts.every((a) => expanded[a.attempt_id]);

  const toggleExpanded = (attemptId: string) => {
    setExpanded((prev) => ({ ...prev, [attemptId]: !prev[attemptId] }));
  };

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    filteredAttempts.forEach((a) => { next[a.attempt_id] = !allExpanded; });
    setExpanded(next);
  };

  const copyGameTimes = () => {
    const times = filteredAttempts.map((a) => `${a.game_time || '—'}${a.period !== '' ? ` P${a.period}` : ''}`).join('\n');
    copyToClipboard(times, 'Game times copied');
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const handleCopyAttemptId = (e: React.MouseEvent | React.KeyboardEvent, attemptId: string) => {
    e.stopPropagation();
    copyToClipboard(attemptId, 'Attempt ID copied');
    setCopiedAttemptId(attemptId);
    setTimeout(() => setCopiedAttemptId(null), 1600);
  };

  const formatGap = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return '—';
    return `${seconds > 0 ? '+' : ''}${seconds.toFixed(2)}s`;
  };

  const gapColor = (seconds: number | null | undefined) => {
    if (seconds === null || seconds === undefined) return Colors.inactive;
    if (seconds > 0) return Colors.secondary;
    if (Math.abs(seconds) > 60) return Colors.error;
    return Colors.text;
  };

  const openImage = (request: OpsSyncRequestDetail) => {
    if (request.image_browser_url) {
      setImageLoadFailed(false);
      setViewingImage(request);
    }
  };

  const handleRefreshImageUrl = async () => {
    if (!viewingImage?.image_object_path) return;
    setRefreshingImageUrl(true);
    const response = await opsSyncApi.refreshImageUrl(viewingImage.image_object_path);
    setRefreshingImageUrl(false);
    if (response.success && response.data?.success && response.data.image_browser_url) {
      setImageLoadFailed(false);
      setViewingImage((prev) => (prev ? { ...prev, image_browser_url: response.data!.image_browser_url } : prev));
    } else {
      toast.error(response.error || response.data?.error || 'Failed to refresh image URL');
    }
  };

  return (
    <div className="section">
      <h2 className="section-title">
        <Radar size={24} />
        Sync Detection
      </h2>

      <div className="teams-editor-tabs">
        {PANEL_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`teams-editor-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'images' && (
        <Card
          title="Sync Images"
          description="Browse the sync-detection screenshots bucket: game type → game → user email → attempt"
        >
          <SyncImagesBrowser />
        </Card>
      )}

      {activeTab === 'detections' && (
      <Card
        title="Sync request troubleshooting"
        description="Live user sync/image-matching attempts, grouped by user then attempt"
      >
        {/* Filters */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(240px, 1.1fr) minmax(200px, 0.9fr) minmax(300px, 1.4fr) auto',
            gap: '14px 16px',
            alignItems: 'start',
            marginBottom: '20px',
          }}
        >
          <div style={{ position: 'relative', minWidth: 0 }}>
            <label style={fieldLabelStyle}>User email</label>
            <div style={{ position: 'relative' }}>
              <AtSign size={16} style={{ position: 'absolute', left: '13px', top: '13px', color: Colors.inactive }} />
              <input
                className="form-input"
                style={{ paddingLeft: '38px', paddingRight: userQuery ? '38px' : '16px' }}
                value={userQuery}
                onChange={(e) => { setUserQuery(e.target.value); setUserMenuOpen(true); }}
                onFocus={() => setUserMenuOpen(true)}
                onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
                placeholder="Start typing an email…"
              />
              {userQuery && (
                <button
                  type="button"
                  onClick={() => { setUserQuery(''); setSelectedUser(null); setUserMenuOpen(false); }}
                  style={{
                    position: 'absolute', right: '8px', top: '8px', width: '26px', height: '26px',
                    borderRadius: '999px', border: 0, background: 'rgba(var(--color-border-rgb), 0.12)',
                    display: 'grid', placeItems: 'center', cursor: 'pointer', color: Colors.textLight,
                  }}
                >
                  <X size={13} />
                </button>
              )}
            </div>
            {userMenuOpen && !selectedUser && (
              <div style={{
                position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, marginTop: '6px',
                background: '#FFFFFF', border: '1px solid rgba(var(--color-border-rgb), 0.24)', borderRadius: '10px',
                boxShadow: '0 16px 34px rgba(21, 41, 92, 0.16)', overflow: 'hidden', maxHeight: '260px', overflowY: 'auto',
              }}
              >
                <div style={{ padding: '9px 14px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: Colors.textLight, background: Colors.background }}>
                  {userQuery ? `${userMatches.length} of ${users.length} active users match "${userQuery.trim()}"` : `${users.length} active users in the last ${formatRangeLabel(effectiveMinutes)}`}
                </div>
                {userMatches.map((u) => (
                  <button
                    key={u.user_email}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setSelectedUser(u.user_email); setUserQuery(u.user_email); setUserMenuOpen(false); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px',
                      border: 0, borderTop: '1px solid rgba(var(--color-border-rgb), 0.12)', background: 'transparent',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <span style={{
                      width: '24px', height: '24px', borderRadius: '999px', flex: 'none', display: 'grid', placeItems: 'center',
                      fontSize: '10.5px', fontWeight: 700,
                      background: u.failures > 0 ? withAlpha(Colors.error, 0.16) : withAlpha(Colors.success, 0.16),
                      color: u.failures > 0 ? Colors.error : Colors.success,
                    }}
                    >
                      {u.user_email.slice(0, 2).toUpperCase()}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '13px', fontFamily: "'SF Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.user_email}</span>
                      <LocationTag location={locationFor(u.user_email)} maxWidth="100%" />
                    </span>
                    <AttributionBadge attribution={attributionFor(u.user_email)} size={18} />
                    <span style={{ fontSize: '12px', color: Colors.textLight, flex: 'none' }}>{u.total_requests} req</span>
                    <span className="status-badge status-success" style={{ padding: '2px 8px', fontSize: '11px', flex: 'none' }}>{u.total_requests - u.failures} success</span>
                    {u.failures > 0 && (
                      <span className="status-badge status-error" style={{ padding: '2px 8px', fontSize: '11px', flex: 'none' }}>{u.failures} fail</span>
                    )}
                  </button>
                ))}
                {userMatches.length === 0 && (
                  <div style={{ padding: '14px', fontSize: '13px', color: Colors.textLight }}>No active user matches that text in this window.</div>
                )}
              </div>
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            <label style={fieldLabelStyle}>Game</label>
            <select
              className="form-input"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All games</option>
              {gameOptions.map((g) => (
                <option key={g.game_name} value={g.game_name}>
                  {g.game_name} ({g.requests})
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: 0 }}>
            <label style={fieldLabelStyle}>Time range</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  display: 'inline-flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '3px',
                  background: Colors.background,
                  borderRadius: '999px',
                  padding: '3px',
                  width: 'fit-content',
                  maxWidth: '100%',
                }}
              >
                {RANGE_OPTIONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRangeMode('preset');
                      setRange(r);
                    }}
                    style={{
                      height: '32px',
                      padding: '0 14px',
                      borderRadius: '999px',
                      border: 0,
                      cursor: 'pointer',
                      fontSize: '12.5px',
                      fontWeight: 700,
                      background: rangeMode === 'preset' && range === r ? '#FFFFFF' : 'transparent',
                      color: rangeMode === 'preset' && range === r ? Colors.text : Colors.textLight,
                      boxShadow: rangeMode === 'preset' && range === r ? '0 1px 3px rgba(21, 41, 92, 0.16)' : 'none',
                    }}
                  >
                    {formatRangeLabel(r)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setRangeMode('custom');
                    setCustomFrom(defaultCustomFromValue());
                  }}
                  style={{
                    height: '32px',
                    padding: '0 14px',
                    borderRadius: '999px',
                    border: 0,
                    cursor: 'pointer',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    background: rangeMode === 'custom' ? '#FFFFFF' : 'transparent',
                    color: rangeMode === 'custom' ? Colors.text : Colors.textLight,
                    boxShadow: rangeMode === 'custom' ? '0 1px 3px rgba(21, 41, 92, 0.16)' : 'none',
                  }}
                >
                  Custom
                </button>
              </div>

              {rangeMode === 'custom' && (
                <>
                  <input
                    className="form-input"
                    type="datetime-local"
                    value={customFrom}
                    max={toDatetimeLocalValue(new Date())}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    style={{
                      width: '210px',
                      height: '38px',
                      padding: '0 10px',
                      fontSize: '13px',
                      borderColor: customRangeInvalid ? Colors.error : undefined,
                    }}
                  />
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      height: '32px',
                      padding: '0 12px',
                      borderRadius: '999px',
                      background: customRangeInvalid
                        ? withAlpha(Colors.error, 0.1)
                        : withAlpha(Colors.accent, 0.1),
                      color: customRangeInvalid ? Colors.error : Colors.text,
                      fontSize: '12.5px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {customRangeInvalid || liveCustomMinutes === null
                      ? 'Invalid from time'
                      : `Showing data for ${formatRangeLabel(liveCustomMinutes)}`}
                  </span>
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', paddingTop: '22px', flexWrap: 'wrap' }}>
            <Button
              className="btn-primary"
              onClick={() => { (selectedUser ? fetchAttempts(selectedUser, true) : fetchUsers(true)); fetchGames(); }}
              loading={selectedUser ? attemptsLoading : usersLoading}
              disabled={customRangeInvalid}
            >
              <Search size={16} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setUserQuery('');
                setSelectedUser(null);
                setGameName('');
                setRangeMode('preset');
                setRange(60);
                setCustomFrom(defaultCustomFromValue());
                setResultFilter(new Set());
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        {selectedUser && resultOptions.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={fieldLabelStyle}>Filter by result</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {resultOptions.map((value) => {
                const active = resultFilter.has(value);
                const tone = RESULT_TONE[value] || 'status-warning';
                const toneColor = tone === 'status-success' ? Colors.success : tone === 'status-error' ? Colors.error : '#FF9500';
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleResultFilter(value)}
                    style={{
                      height: '28px', padding: '0 12px', borderRadius: '999px', cursor: 'pointer',
                      fontSize: '11.5px', fontWeight: 700, fontFamily: "'SF Mono', monospace",
                      border: `1px solid ${active ? toneColor : 'rgba(var(--color-border-rgb), 0.24)'}`,
                      background: active ? withAlpha(toneColor, 0.14) : '#FFFFFF',
                      color: active ? toneColor : Colors.textLight,
                    }}
                  >
                    {value}
                  </button>
                );
              })}
              {resultFilter.size > 0 && (
                <button
                  type="button"
                  onClick={() => setResultFilter(new Set())}
                  style={{
                    height: '28px', padding: '0 10px', borderRadius: '999px', border: 0, cursor: 'pointer',
                    fontSize: '11.5px', fontWeight: 700, background: 'transparent', color: Colors.secondary,
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {(usersError && !selectedUser) && (
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <AlertCircle size={20} />
            <div>{usersError}</div>
          </div>
        )}
        {(attemptsError && selectedUser) && (
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <AlertCircle size={20} />
            <div>{attemptsError}</div>
          </div>
        )}

        {/* Stats */}
        {!selectedUser && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            {stats.map((s) => (
              <div key={s.label} className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px', boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.16)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: Colors.textLight }}>{s.label}</span>
                  <s.icon size={16} style={{ color: s.tone || Colors.inactive }} />
                </div>
                <div style={{ fontSize: '26px', fontWeight: 700, color: s.tone || Colors.text }}>{s.value}</div>
                <div style={{ fontSize: '12px', color: Colors.textLight }}>{s.sub}</div>
              </div>
            ))}
          </div>
        )}

        {selectedUser && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '20px' }}>
            {[
              { label: 'Total attempts', value: attemptStats.attempts, icon: ListChecks },
              { label: 'Success', value: attemptStats.success, icon: CheckCircle2, tone: Colors.success },
              { label: 'Failures', value: attemptStats.failures, icon: AlertTriangle, tone: Colors.error },
            ].map((s) => (
              <div key={s.label} className="card" style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '6px', boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.16)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: Colors.textLight }}>{s.label}</span>
                  <s.icon size={16} style={{ color: s.tone || Colors.inactive }} />
                </div>
                <div style={{ fontSize: '26px', fontWeight: 700, color: s.tone || Colors.text }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Unsynced Users Alert Section */}
        {!selectedUser && unsyncedUsers.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <AlertTriangle size={18} style={{ color: Colors.error }} />
              <h3 className="card-title" style={{ fontSize: '15px', margin: 0, color: Colors.error }}>
                Users who did not get sync ({unsyncedUsers.length})
              </h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px' }}>
              {unsyncedUsers.map((u) => (
                <button
                  key={u.user_email}
                  type="button"
                  onClick={() => { setSelectedUser(u.user_email); setUserQuery(u.user_email); }}
                  className="card"
                  style={{
                    textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '10px',
                    padding: '14px 16px', boxShadow: 'none',
                    background: USER_CARD.background,
                    border: `1px solid ${USER_CARD.border}`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      width: '28px', height: '28px', borderRadius: '999px', flex: 'none', display: 'grid', placeItems: 'center',
                      fontSize: '11px', fontWeight: 700, background: withAlpha(Colors.error, 0.16), color: Colors.error,
                    }}
                    >
                      {u.user_email.slice(0, 2).toUpperCase()}
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontFamily: "'SF Mono', monospace", fontSize: '13px', fontWeight: 600, color: Colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {u.user_email}
                    </span>
                    <ChevronRight size={16} style={{ flex: 'none', color: Colors.secondary }} aria-label="Inspect" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span className="status-badge status-error" style={{ fontSize: '11px', padding: '2px 9px' }}>
                      {u.failures === (u.total_attempts || u.total_requests)
                        ? `All ${u.total_attempts || u.total_requests} attempt${(u.total_attempts || u.total_requests) === 1 ? '' : 's'} failed`
                        : `Last attempt failed`}
                    </span>
                    {u.last_attempt_result && (
                      <span className={`status-badge ${RESULT_TONE[u.last_attempt_result] || 'status-warning'}`} style={{ fontSize: '11px', padding: '2px 9px', fontFamily: "'SF Mono', monospace" }}>
                        {u.last_attempt_result}
                      </span>
                    )}
                  </div>
                  {renderUserCardFooter(u.user_email)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Active users grid */}
        {!selectedUser && (
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
              <div>
                <h3 className="card-title" style={{ fontSize: '15px', margin: 0 }}>Users trying to sync</h3>
                <p className="card-description" style={{ margin: '2px 0 0' }}>Grouped from user_id · pick one to see attempts</p>
              </div>
              <span style={{ fontSize: '12px', color: Colors.textLight }}>Sorted by attempts</span>
            </div>
            {users.length === 0 && !usersLoading && !usersError && (
              <div style={{ textAlign: 'center', padding: '40px', color: Colors.textLight }}>
                <Radar size={28} style={{ opacity: 0.4, marginBottom: '8px' }} />
                <div>No sync activity in the last {formatRangeLabel(range)}</div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '12px' }}>
              {users.map((u) => {
                const totalAtt = u.total_attempts || u.total_requests;
                const succAtt = Math.max(0, totalAtt - u.failures);
                return (
                  <button
                    key={u.user_email}
                    type="button"
                    onClick={() => { setSelectedUser(u.user_email); setUserQuery(u.user_email); }}
                    className="card"
                    style={{
                      textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '10px',
                      padding: '14px 16px', boxShadow: 'none',
                      background: USER_CARD.background,
                      border: `1px solid ${USER_CARD.border}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{
                        width: '28px', height: '28px', borderRadius: '999px', flex: 'none', display: 'grid', placeItems: 'center',
                        fontSize: '11px', fontWeight: 700, background: withAlpha((u.failures > 0 ? Colors.error : Colors.success), 0.16), color: (u.failures > 0 ? Colors.error : Colors.success),
                      }}
                      >
                        {u.user_email.slice(0, 2).toUpperCase()}
                      </span>
                      <span style={{ flex: 1, minWidth: 0, fontFamily: "'SF Mono', monospace", fontSize: '13px', fontWeight: 600, color: Colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.user_email}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, background: Colors.background, color: Colors.text, borderRadius: '999px', padding: '2px 9px' }}>{totalAtt} attempts</span>
                      <span style={{ fontSize: '11px', fontWeight: 700, background: withAlpha(Colors.success, 0.14), color: Colors.success, borderRadius: '999px', padding: '2px 9px' }}>{succAtt} success</span>
                      {u.failures > 0 && (
                        <span style={{ fontSize: '11px', fontWeight: 700, background: withAlpha(Colors.error, 0.14), color: Colors.error, borderRadius: '999px', padding: '2px 9px' }}>{u.failures} fail</span>
                      )}
                      {u.is_last_attempt_failed && u.last_attempt_result && (
                        <span className={`status-badge ${RESULT_TONE[u.last_attempt_result] || 'status-warning'}`} style={{ fontSize: '11px', padding: '2px 9px', fontFamily: "'SF Mono', monospace" }}>
                          Last: {u.last_attempt_result}
                        </span>
                      )}
                    </div>
                    {renderUserCardFooter(u.user_email)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Selected user drill-down */}
        {selectedUser && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{
              background: Colors.background, border: '1px solid rgba(var(--color-border-rgb), 0.2)', borderRadius: '12px',
              padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
            }}
            >
              <button
                type="button"
                onClick={() => { setSelectedUser(null); setUserQuery(''); }}
                style={{
                  width: '32px', height: '32px', borderRadius: '999px', border: '1px solid rgba(var(--color-border-rgb), 0.3)',
                  background: '#FFFFFF', display: 'grid', placeItems: 'center', cursor: 'pointer', color: Colors.text, flex: 'none',
                }}
              >
                <ArrowLeft size={16} />
              </button>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "'SF Mono', monospace", fontSize: '15px', fontWeight: 600 }}>{selectedUser}</div>
                <div style={{ fontSize: '12px', color: Colors.textLight, marginTop: '2px' }}>
                  {attemptStats.attempts} attempt{attemptStats.attempts === 1 ? '' : 's'} · {attemptStats.success} success · {attemptStats.failures} fail · last {formatRangeLabel(range)}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                <Button variant="outline" size="small" onClick={expandAll}>{allExpanded ? 'Collapse all' : 'Expand all'}</Button>
                <Button variant="outline" size="small" onClick={copyGameTimes}>
                  <Copy size={14} />
                  {copied ? 'Copied' : 'Copy game times'}
                </Button>
              </div>
            </div>

            {filteredAttempts.length === 0 && !attemptsLoading && !attemptsError && (
              <div style={{ textAlign: 'center', padding: '40px', color: Colors.textLight }}>
                {attempts.length === 0
                  ? `No sync attempts for this user in the last ${formatRangeLabel(range)}.`
                  : 'No requests match the selected result filter.'}
              </div>
            )}

            {filteredAttempts.map((a) => {
              const isOpen = !!expanded[a.attempt_id];
              const tone = ATTEMPT_TONE[a.status] || 'status-warning';
              const borderColor = a.status === 'success' ? Colors.success : a.status === 'partial' ? '#FF9500' : Colors.error;
              return (
                <div
                  key={a.attempt_id}
                  className="card"
                  style={{
                    padding: 0, overflow: 'hidden', boxShadow: 'none',
                    border: '1px solid rgba(var(--color-border-rgb), 0.2)',
                    borderLeft: `4px solid ${borderColor}`,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleExpanded(a.attempt_id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px',
                      border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <ChevronRight size={16} style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: Colors.textLight, flex: 'none' }} />
                    <span style={{ minWidth: 0, flex: '1 1 360px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: "'SF Mono', monospace", fontWeight: 700, fontSize: '13px' }}>
                          Game clock {a.game_time || '—'}{a.period !== '' ? ` P${a.period}` : ''}
                        </span>
                        {a.attempt_id && (
                          <span
                            style={{
                              fontSize: '11px',
                              fontWeight: 500,
                              fontFamily: "'SF Mono', monospace",
                              // color: Colors.textLight,
                              // background: Colors.background,
                              padding: '2px 8px',
                              borderRadius: '6px',
                              border: '1px solid rgba(var(--color-border-rgb), 0.16)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              maxWidth: '320px',
                            }}
                            title={`Attempt ID: ${a.attempt_id}`}
                          >
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              Attempt Id: {a.attempt_id}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => handleCopyAttemptId(e, a.attempt_id)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCopyAttemptId(e, a.attempt_id); }}
                              style={{
                                border: 0,
                                background: 'transparent',
                                padding: '2px',
                                margin: 0,
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: copiedAttemptId === a.attempt_id ? Colors.success : Colors.secondary,
                                borderRadius: '4px',
                              }}
                              title="Copy Attempt ID"
                            >
                              {copiedAttemptId === a.attempt_id ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                            </span>
                          </span>
                        )}
                      </span>
                      <span style={{ display: 'block', fontSize: '11.5px', color: Colors.textLight, marginTop: '2px' }}>{a.game_id} · First: {a.first_request} · Last: {a.last_request}</span>
                    </span>
                    <span className={`status-badge ${tone}`} style={{ flex: 'none' }}>{a.status}</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: Colors.success, background: Colors.background, borderRadius: '999px', padding: '3px 11px', flex: 'none' }}>{a.success_count} success</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: Colors.error, background: Colors.background, borderRadius: '999px', padding: '3px 11px', flex: 'none' }}>{a.failure_count} failed</span>
                  </button>

                  {isOpen && (
                    <div style={{ padding: '0 16px 16px' }}>
                      <div className="table-scroll-wrap" style={{ border: '1px solid rgba(var(--color-border-rgb), 0.16)', borderRadius: '8px', overflow: 'auto' }}>
                        <table className="table" style={{ marginBottom: 0, minWidth: '760px' }}>
                          <colgroup>
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '16%' }} />
                            <col style={{ width: '16%' }} />
                            <col style={{ width: '13%' }} />
                            <col style={{ width: '11%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '19%' }} />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Timestamp UTC</th>
                              <th>Result</th>
                              <th>Game clock</th>
                              <th>Sync gap</th>
                              <th>Matches</th>
                              <th>Proc</th>
                              <th>Screenshot</th>
                            </tr>
                          </thead>
                          <tbody>
                            {a.requests.map((r, i) => (
                              <React.Fragment key={r.id || `${a.attempt_id}-${i}`}>
                              <tr>
                                <td>{i + 1}</td>
                                <td style={{ fontFamily: "'SF Mono', monospace" }}>{r.created_at}</td>
                                <td><span className={`status-badge ${RESULT_TONE[r.result] || 'status-warning'}`} style={{ fontFamily: "'SF Mono', monospace", fontSize: '11px', padding: '2px 9px' }}>{r.result}</span></td>
                                <td style={{ fontFamily: "'SF Mono', monospace", fontWeight: 600 }}>{r.game_time}{r.period !== '' ? ` P${r.period}` : ''}</td>
                                <td style={{ fontFamily: "'SF Mono', monospace", color: gapColor(r.time_difference_seconds) }}>{formatGap(r.time_difference_seconds)}</td>
                                <td style={{ fontFamily: "'SF Mono', monospace" }}>{r.genius_matches}</td>
                                <td style={{ fontFamily: "'SF Mono', monospace" }}>{Math.round((r.processing_time_seconds || 0) * 1000)}ms</td>
                                <td>
                                  {r.image_browser_url ? (
                                    <Button variant="outline" size="small" style={{ padding: '0 10px', height: '26px' }} onClick={() => openImage(r)}>
                                      <ImageIcon size={13} />
                                      View
                                    </Button>
                                  ) : (
                                    <span style={{ fontSize: '12px', color: Colors.inactive }}>not stored</span>
                                  )}
                                </td>
                              </tr>
                              {r.llm_fallback_used && (
                                <tr>
                                  <td colSpan={8} style={{ paddingTop: 0, borderTop: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'stretch', gap: '14px', background: Colors.background, borderRadius: '8px', padding: '8px 12px' }}>
                                      <span style={{ alignSelf: 'center', flex: 'none', fontSize: '11px', fontWeight: 700, color: Colors.secondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                        LLM fallback
                                      </span>
                                      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '6px 16px' }}>
                                        {llmFallbackFields(r).map((field) => (
                                          <div key={field.label} style={{ minWidth: 0 }}>
                                            <div style={{ fontSize: '10.5px', fontWeight: 600, color: Colors.textLight, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{field.label}</div>
                                            <div style={{ fontFamily: "'SF Mono', monospace", fontSize: '12px', fontWeight: 600, color: Colors.text, marginTop: '2px', overflowWrap: 'anywhere' }}>{field.value}</div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
      )}

      {viewingImage && (
        <div className="games-modal-overlay" onClick={() => setViewingImage(null)}>
          <div className="games-modal-panel" style={{ width: 'min(720px, calc(100vw - 32px))' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: Colors.text }}>Screenshot</div>
                <div style={{ fontSize: '12px', color: Colors.textLight, marginTop: '2px' }}>
                  {viewingImage.id} · {viewingImage.created_at} · {viewingImage.game_time}{viewingImage.period !== '' ? ` P${viewingImage.period}` : ''}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setViewingImage(null)}
                style={{
                  width: '30px', height: '30px', borderRadius: '999px', border: '1px solid rgba(var(--color-border-rgb), 0.24)',
                  background: '#FFFFFF', display: 'grid', placeItems: 'center', cursor: 'pointer', color: Colors.text, flex: 'none',
                }}
              >
                <X size={15} />
              </button>
            </div>

            {imageLoadFailed ? (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', padding: '48px 16px',
                color: Colors.textLight, background: Colors.background, borderRadius: '10px',
              }}
              >
                <ImageOff size={28} style={{ opacity: 0.5 }} />
                <span style={{ fontSize: '13px' }}>Unable to load this image — the link may have expired.</span>
                <Button variant="outline" size="small" onClick={handleRefreshImageUrl} loading={refreshingImageUrl} disabled={!viewingImage.image_object_path}>
                  <RefreshCw size={13} />
                  Refresh image link
                </Button>
              </div>
            ) : (
              <img
                src={viewingImage.image_browser_url}
                alt={`Sync screenshot for request ${viewingImage.id}`}
                onError={() => setImageLoadFailed(true)}
                style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '10px', background: Colors.background, display: 'block' }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
